import { useMemo } from 'react';

import { Box, Dialog, DialogContent, DialogTitle, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { InsuranceCompany, MatchingProfileData } from '@uplift/api';
import { TimeFormats } from '@uplift/constants';
import { useTimezone } from '@uplift/contexts';
import { useL10n } from '@uplift/hooks';
import { DateObject } from '@uplift/services';
import { ProviderMedicalRole } from '@uplift/types';
import { UserImage, Button, SupportPaymentLabel } from '@uplift/ui-v2';

import { useSelectedProviderStyles } from '../../styles';

export type ScheduleSessionModalProps = {
  onClose: () => void;
  providerItem: MatchingProfileData;
  selectedScheduleSessionDate: DateObject;
  onScheduleSession: () => void;
  matchedInsuranceCompany: InsuranceCompany | null;
  isBookingSponsored?: boolean;
  isStudentSecondBooking?: boolean;
};

export const ScheduleSessionModal: React.VFC<ScheduleSessionModalProps> = ({
  onClose,
  providerItem: provider,
  selectedScheduleSessionDate,
  onScheduleSession,
  matchedInsuranceCompany,
  isBookingSponsored = false,
  isStudentSecondBooking = false,
}) => {
  const l10n = useL10n();
  const { classes: modalClasses } = useModalStyles();
  const { classes } = useSelectedProviderStyles();
  const timezone = useTimezone();
  const displayName = provider?.nameDisplay;
  const avatarUrl = provider.avatarAsset?.url;
  const providerName = useMemo(() => {
    if (!displayName?.defaultDisplay) {
      return '';
    }

    return displayName.defaultDisplay;
  }, [ displayName?.defaultDisplay ]);

  const selected = selectedScheduleSessionDate.tz(timezone);

  const medicalRole = provider?.user?.medicalRole;
  const isTherapist = medicalRole === ProviderMedicalRole.therapist;
  const isPsychiatrist = medicalRole === ProviderMedicalRole.psychiatrist;
  const weekday = selected.format('dddd');
  const time = selected.format(TimeFormats['h:mm a']);
  const sessionDateText = `${isTherapist ? 'Every ' : ''}${weekday} at ${time}`;
  const titleMessage = useMemo(() => {
    if (isStudentSecondBooking) {
      return isTherapist
        ? l10n.matching.selectedProvider.scheduleSession
        : l10n.matching.selectedProvider.confirmConsultation;
    }
    return isTherapist
      ? l10n.matching.selectedProvider.scheduleSession
      : l10n.matching.selectedProvider.bookConsultation;
  }, [ isTherapist, isStudentSecondBooking, l10n ]);

  const bookingMessage = useMemo(() => {
    if (isStudentSecondBooking) {
      return isTherapist
        ? l10n.matching.selectedProvider.confirmWeeklySession
        : l10n.matching.selectedProvider.confirm;
    }
    return isTherapist
      ? l10n.matching.selectedProvider.bookWeeklySession
      : l10n.matching.selectedProvider.bookConsultation;
  }, [ isTherapist, isStudentSecondBooking, l10n ]);

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="sm"
      classes={modalClasses}
    >
      <DialogTitle className={classes.modalTitle}>{titleMessage}</DialogTitle>
      <DialogContent className={classes.modalContent}>
        {isTherapist && <Typography>{l10n.matching.selectedProvider.skipOrRescheduleAsNeeded}</Typography>}

        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          gap="0.5rem"
          pt={3}
          pb={{ xs: 3, sm: 2 }}
        >
          <Box display="flex">
            <UserImage imageUrl={avatarUrl} maxWidth="128px" borderRadius="4px" />
          </Box>
          <Typography>{l10n.matching.selectedProvider.meetWithProvider(providerName)}</Typography>
          <Typography color="secondary" className={classes.fontWeight600}>{sessionDateText}</Typography>

          {!matchedInsuranceCompany && (
            <Box mt={1}>
              <SupportPaymentLabel
                isAcceptInsurance={false}
                className={classes.paymentLabel}
                isPsychiatrist={medicalRole === ProviderMedicalRole.psychiatrist}
                isBookingSponsored={isBookingSponsored}
              />
            </Box>
          )}
        </Box>

      </DialogContent>
      <Box className={classes.modalActions}>
        <Button variant="contained" color="secondary" fullWidth onClick={onScheduleSession}>{bookingMessage}</Button>
        <Button fullWidth onClick={onClose}>{l10n.general.cancel}</Button>
        {!isBookingSponsored && (
          <Box mt={{ sm: 1 }} textAlign="center">
            <Typography color="textSecondary">{l10n.matching.selectedProvider.billedUntilAfterFirstSession}</Typography>
          </Box>
        )}
        {isPsychiatrist && (
          <Typography color="textSecondary" fontStyle="italic" fontSize=".75rem" textAlign="center">
            {l10n.findPsychiatrist.noCSWarning}
          </Typography>
        )}
      </Box>
    </Dialog>
  );
};

const useModalStyles = makeStyles()((theme) => ({
  paper: {
    margin: theme.spacing(2)
  },
  paperWidthSm: {
    width: '100%',
    maxWidth: '382px'
  },
}));
